import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, Cell, ReferenceLine } from "recharts";
import { fmt2 } from "@/lib/api";
import { numLocale } from "@/lib/num";
import { fl, monthShort, shortIDR } from "@/lib/finI18n";
import { exportRows } from "@/components/TableTools";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, AlertTriangle } from "lucide-react";

export const pct = (v) => (v === null || v === undefined ? "—" : `${Number(v).toLocaleString(numLocale(), { maximumFractionDigits: 2 })}%`);

export function Kpi({ label, value, testid, tone = "" }) {
  return (
    <div className="rounded-md border border-border p-3">
      <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`num text-lg font-semibold mt-1 ${tone}`} data-testid={testid}>{value}</p>
    </div>
  );
}

export function RoiChart({ months, lang, testid = "roi-chart" }) {
  const data = months.map((m) => ({ name: monthShort(m.month, lang), profit: m.future ? null : m.net_profit, roi: m.future ? null : m.roi, ytd: m.future ? null : m.roi_ytd }));
  return (
    <div className="h-72" data-testid={testid}>
      <ResponsiveContainer>
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="rp" tick={{ fontSize: 11 }} tickFormatter={shortIDR} />
          <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
          <ReferenceLine yAxisId="rp" y={0} stroke="hsl(var(--muted-foreground))" />
          <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v, k) => (k === fl("roiProfit", lang) ? fmt2(v) : pct(v))} />
          <Legend wrapperStyle={{ fontSize: 12 }} />
          <Bar yAxisId="rp" dataKey="profit" name={fl("roiProfit", lang)} fill="#059669" radius={[4, 4, 0, 0]}>
            {data.map((d, i) => <Cell key={i} fill={(d.profit || 0) < 0 ? "#e11d48" : "#059669"} />)}
          </Bar>
          <Line yAxisId="pct" type="monotone" dataKey="roi" name={fl("roiMonthly", lang)} stroke="#2563eb" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
          <Line yAxisId="pct" type="monotone" dataKey="ytd" name={fl("roiYtd", lang)} stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} connectNulls={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RoiView({ report, lang, year, onYear }) {
  const years = Array.from({ length: 8 }, (_, i) => new Date().getFullYear() - i);
  const exp = () => exportRows(report.months, [[fl("roiMonth", lang), (m) => monthShort(m.month, lang)], [fl("roiProfit", lang), "net_profit"], [fl("roiCum", lang), "cum_profit"],
    [fl("roiInvested", lang), "invested"], [`${fl("roiMonthly", lang)} (%)`, "roi"], [`${fl("roiYtd", lang)} (%)`, "roi_ytd"]], `roi_${year}`);
  return (
    <Card className="p-4 space-y-4" data-testid="roi-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="max-w-2xl">
          <h2 className="font-head font-semibold">{fl("roiTitle", lang)} {year}</h2>
          <p className="text-xs text-muted-foreground mt-1" data-testid="roi-formula">{fl("roiFormula", lang)} {report.accounts.map((a) => `${a.code} ${a.name}`).join(", ")}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => onYear(Number(v))}>
            <SelectTrigger className="w-28 h-9" data-testid="roi-year"><SelectValue /></SelectTrigger>
            <SelectContent>{years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button variant="outline" size="sm" className="h-9" onClick={exp} data-testid="roi-export"><Download className="h-4 w-4 mr-1" />Excel</Button>
        </div>
      </div>
      {report.invested <= 0 && <p className="flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid="roi-no-capital"><AlertTriangle className="h-4 w-4" />{fl("roiNoCapital", lang)}</p>}
      <div className="grid gap-3 sm:grid-cols-3">
        <Kpi label={fl("roiNet", lang)} value={fmt2(report.net_profit)} testid="roi-net-profit" tone={report.net_profit < 0 ? "text-rose-600" : ""} />
        <Kpi label={fl("roiInvested", lang)} value={fmt2(report.invested)} testid="roi-invested" />
        <Kpi label={fl("roiYtd", lang)} value={pct(report.roi)} testid="roi-value" tone={(report.roi || 0) < 0 ? "text-rose-600" : "text-emerald-700"} />
      </div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>{fl("roiMonth", lang)}</TableHead><TableHead className="text-right">{fl("roiProfit", lang)}</TableHead><TableHead className="text-right">{fl("roiCum", lang)}</TableHead>
            <TableHead className="text-right">{fl("roiInvested", lang)}</TableHead><TableHead className="text-right">{fl("roiMonthly", lang)}</TableHead><TableHead className="text-right">{fl("roiYtd", lang)}</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {report.months.map((m) => (
              <TableRow key={m.month} className={m.future ? "opacity-40" : ""} data-testid={`roi-row-${m.month}`}>
                <TableCell className="text-xs">{monthShort(m.month, lang)} {report.year}</TableCell>
                <TableCell className={`num text-right text-xs ${m.net_profit < 0 ? "text-rose-600" : ""}`}>{fmt2(m.net_profit)}</TableCell>
                <TableCell className="num text-right text-xs">{fmt2(m.cum_profit)}</TableCell>
                <TableCell className="num text-right text-xs">{fmt2(m.invested)}</TableCell>
                <TableCell className="num text-right text-xs">{m.future ? "—" : pct(m.roi)}</TableCell>
                <TableCell className="num text-right text-xs font-medium">{m.future ? "—" : pct(m.roi_ytd)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Card>
  );
}
